import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, AlertCircle, Loader } from "lucide-react";

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

function usePaymentPoller(sessionId) {
  const [state, setState] = useState({ status: "polling" });

  const startPolling = useCallback(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return () => {};
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/projects/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setState({ status: "timeout" });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setState({ status: "error", message: e.message });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    const cleanup = startPolling();
    return cleanup;
  }, [startPolling]);

  return state;
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const projectId = params.get("project_id") || params.get("order_id");
  const state = usePaymentPoller(sessionId);

  let content;
  if (state.status === "polling") {
    content = (
      <>
        <Loader className="text-gold mx-auto mb-4 animate-spin" size={48} />
        <h2 className="font-heading text-2xl mb-2">Confirming Payment…</h2>
        <p className="text-white/60">Hang tight — we&apos;re verifying your transaction.</p>
      </>
    );
  } else if (state.status === "paid") {
    content = (
      <>
        <CheckCircle2 className="text-gold mx-auto mb-4" size={56} data-testid="payment-success" />
        <h2 className="font-heading text-2xl mb-2">Payment Confirmed</h2>
        <p className="text-white/65 mb-6">
          ${state.data?.amount?.toFixed(2)} paid. Your songs are unlocked and ready to download.
        </p>
        <Link to={`/dashboard/projects/${projectId}`} className="btn-gold" data-testid="success-go-to-project">
          View My Songs
        </Link>
      </>
    );
  } else {
    let body = state.message || "We couldn't confirm the payment.";
    if (state.status === "expired") body = "The checkout session expired.";
    else if (state.status === "timeout") body = "Payment is taking longer than expected. Check your dashboard shortly.";
    content = (
      <>
        <AlertCircle className="text-destructive mx-auto mb-4" size={48} data-testid="payment-error" />
        <h2 className="font-heading text-2xl mb-2">Something Went Wrong</h2>
        <p className="text-white/60 mb-6">{body}</p>
        <Link to={`/dashboard/projects/${projectId}`} className="btn-outline">Back to Project</Link>
      </>
    );
  }

  return (
    <div className="bg-black min-h-[calc(100vh-80px)] flex items-center justify-center px-6">
      <div className="bg-ink-900 border border-ink-700 p-10 max-w-md w-full text-center">
        {content}
      </div>
    </div>
  );
}
